import { isDefined, isNotDefined } from '@sgme/fp';

import { workspaceApi } from '@/api/workspace/index.ts';
import { cashApi } from '@/store/cash/api/index.ts';
import { espApi } from '@/store/esp/api';
import { espSlice } from '@/store/esp/slice.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { uniq } from '@/utils/array.ts';

export const registerCashChangeCurrenciesForGettingEspLimitsListeners = (startAppListening: StartAppListening) => {
  // Listener to fetch ESP limits when workspace is loaded
  startAppListening({
    matcher: workspaceApi.endpoints.getWorkspace.matchFulfilled,
    effect: async (action, api) => {
      const workspace = action.payload;

      if (isNotDefined(workspace)) {
        return;
      }

      const allNewCurrenciesWithoutEspLimits = workspace.products.cash
        .flatMap((tile) => tile.properties.currencyPair?.split('/'))
        .filter(isDefined);

      getEspLimits(allNewCurrenciesWithoutEspLimits, api.getState(), api.dispatch);
    },
  });

  // Listener to fetch ESP limits when currencies are changed in cash tiles
  startAppListening({
    matcher: cashApi.endpoints.checkCashByTradeCapture.matchFulfilled,
    effect: async (action, api) => {
      const {
        tileId,
        properties: { currencyPair },
      } = action.meta.arg?.originalArgs || {};

      const [currency1, currency2] = currencyPair?.split('/') || [];

      if (isNotDefined(tileId) || isNotDefined(currency1) || isNotDefined(currency2)) {
        return;
      }

      getEspLimits([currency1, currency2], api.getState(), api.dispatch);
    },
  });
};

const getEspLimits = (allCurrenciesToCheck: string[], state: AppState, dispatch: AppDispatch) => {
  const allCurrenciesWithEspLimits = espSlice.selectors.selectCurrenciesWithEspLimits(state);

  const allNewCurrenciesWithoutEspLimits = uniq(allCurrenciesToCheck).filter(
    (currency) => !allCurrenciesWithEspLimits.includes(currency),
  );

  if (allNewCurrenciesWithoutEspLimits.length === 0) {
    // No new currencies to fetch limits for
    return;
  }

  dispatch(espApi.endpoints.getEspLimits.initiate(allNewCurrenciesWithoutEspLimits));
};
